import { Fragment, ReactNode } from 'react';
import { Divider, HStack } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { Redo, Undo } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { SecondaryHeader } from './SecondaryHeader';
import { EditorSaveStatus } from './EditorSaveStatus';
import { ToolbarIconButton } from './ToolbarIconButton';

export type EditorAction = {
  id: string;
  /** Rendered inline in the toolbar's right zone. */
  node: ReactNode;
  /**
   * Start a new divided group before this action, so it reads as a distinct
   * cluster from the actions before it (e.g. a secondary settings action sitting
   * apart from the primary Publish). Omit to keep it grouped with the preceding
   * action (e.g. Preview + Publish together).
   */
  startsGroup?: boolean;
};

type UndoRedo = {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
};

/**
 * Drives the save status' "out of date" state: when the tab has fallen behind
 * the server, the indicator offers a "load latest" action instead of letting the
 * user edit into a save conflict.
 */
type StaleEditor = {
  isStale: boolean;
  entityLabel: string;
  onLoadLatest: () => void;
};

/**
 * The shared toolbar for the content editors, built on SecondaryHeader. It owns
 * the controls common to every editor (undo/redo and the save status) so they
 * stop diverging between editor types.
 *
 * Left zone: breadcrumbs + title. Right zone: three groups, separated by generous
 * whitespace (no dividers) so they read distinctly, the editing cluster
 * (undo/redo + save status), then `status` (e.g. the published badge), then the
 * editor's `actions` (preview, publish). SecondaryHeader keeps these on one row
 * when wide and wraps the right zone onto a second row beneath the protected
 * title row when narrow, so nothing is hidden. Individual actions decide how they
 * degrade when narrow (e.g. dropping a label to an icon).
 */
export function EditorToolbar({
  breadcrumbs,
  undoRedo,
  staleEditor,
  status,
  actions = [],
}: {
  breadcrumbs?: ReactNode;
  undoRedo?: UndoRedo;
  staleEditor?: StaleEditor;
  status?: ReactNode;
  actions?: EditorAction[];
}) {
  // Save status sits to the LEFT of undo/redo so the buttons anchor against the
  // divider on the right and stay put. The status label changes width as it cycles
  // ("Saved" / "Saving..." / "Unsaved changes"); placing it on the left lets it
  // grow toward the centre rather than shoving the buttons sideways.
  const editStatus = undoRedo ? (
    <HStack spacing="1">
      <EditorSaveStatus
        isStale={staleEditor?.isStale}
        entityLabel={staleEditor?.entityLabel}
        onLoadLatest={staleEditor?.onLoadLatest}
      />
      <ToolbarIconButton
        aria-label={t`Undo`}
        isDisabled={!undoRedo.canUndo}
        onClick={undoRedo.onUndo}
      >
        <Undo size={iconSize.md} color="currentColor" />
      </ToolbarIconButton>
      <ToolbarIconButton
        aria-label={t`Redo`}
        isDisabled={!undoRedo.canRedo}
        onClick={undoRedo.onRedo}
      >
        <Redo size={iconSize.md} color="currentColor" />
      </ToolbarIconButton>
    </HStack>
  ) : null;

  // Split actions into clusters at each `startsGroup` boundary, so a secondary
  // action (e.g. settings) can sit in its own divided segment apart from the
  // primary action (e.g. Publish), while actions left ungrouped (e.g. Preview +
  // Publish) stay together.
  const actionGroups: EditorAction[][] = [];
  for (const action of actions) {
    if (action.startsGroup || actionGroups.length === 0) {
      actionGroups.push([action]);
    } else {
      actionGroups[actionGroups.length - 1]!.push(action);
    }
  }

  const segments: { key: string; node: ReactNode }[] = [];
  if (editStatus) {
    segments.push({ key: 'edit-status', node: editStatus });
  }
  if (status) {
    segments.push({ key: 'status', node: status });
  }
  for (const group of actionGroups) {
    segments.push({
      key: `actions-${group[0]!.id}`,
      node: (
        <HStack spacing="2">
          {group.map((action) => (
            <Fragment key={action.id}>{action.node}</Fragment>
          ))}
        </HStack>
      ),
    });
  }

  return (
    <SecondaryHeader
      left={breadcrumbs}
      right={
        <HStack spacing="3" flexShrink={0}>
          {segments.map((segment, i) => (
            <Fragment key={segment.key}>
              {i > 0 ? <Divider orientation="vertical" h="6" /> : null}
              {segment.node}
            </Fragment>
          ))}
        </HStack>
      }
    />
  );
}
